import { useQueryClient } from "@tanstack/react-query";
import isEmpty from "lodash/isEmpty";
import { usePostHog } from "posthog-js/react";
import { useReducer, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import usePublicApi from "@/core/api/usePublicApi";
import { userKey } from "@/user/query-keys";
import { DetailedUser } from "@/user/user";
import { getEnrichedUser, isApOnly } from "@/user/utils";
import { IUser, LoginCredentials, ResetPasswordCredentials } from "./auth";
import AuthContext from "./auth-context";
import { authReducer } from "./auth-reducer";
import {
  getPasswordResetSubmissionError,
  isPasswordResetThrottledError,
  PasswordResetError,
  PASSWORD_RESET_ERROR_CODE,
} from "./password-reset-error";
import { refreshAcrossTabs } from "./refresh-across-tabs";
import { getTokenRole } from "./token-role";
import { usePasswordReset } from "./use-password-reset";

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const storedToken = localStorage.getItem("token");
  const storedUser = localStorage.getItem("user");
  const posthog = usePostHog();
  const refreshPromise = useRef<Promise<string | null> | null>(null);
  const publicApi = usePublicApi();
  const queryClient = useQueryClient();

  const [state, dispatch] = useReducer(authReducer, {
    user: storedUser ? JSON.parse(storedUser) : null,
    token: storedToken,
    isAuthenticated: !!storedToken,
    isLoading: false,
    forgotPasswordEmail: null,
    forgotPasswordVerificationCode: null,
    resetCodeResendAvailableAt: {},
    isImpersonating: false,
    impersonatedUser: null,
  });

  const {
    getResetCodeResendAvailableAt,
    openResetCodeEntry,
    sendPasswordResetCode,
    forgotPassword,
    verifyResetPasswordCode,
    resetPasswordCode,
  } = usePasswordReset({
    dispatch,
    resetCodeResendAvailableAt: state.resetCodeResendAvailableAt,
    posthog,
  });

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      dispatch({ type: "SET_LOADING" });
      try {
        posthog?.capture("login_attempted", { email: credentials.email });

        const response = await publicApi.post("/login", {
          user: { ...credentials },
        });

        const userData = response.data?.data;
        const token = response.headers.authorization?.replace("Bearer ", "");

        if (userData?.needs_password_reset) {
          posthog?.capture("password_reset_required", {
            email: credentials.email,
          });

          const resetError = await sendPasswordResetCode(credentials.email);
          if (resetError && !isPasswordResetThrottledError(resetError)) {
            throw resetError;
          }

          openResetCodeEntry(credentials.email);
          return;
        }

        if (!token || isEmpty(userData)) throw new Error("No token received");

        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(userData));

        // Identify user in PostHog with all available fields
        posthog?.identify(userData.id, {
          email: userData.email,
          distinct_id: userData.id,
          first_name: userData.first_name,
          last_name: userData.last_name,
          role: userData.role,
          parentable_id: userData.parentable_id,
          parentable_type: userData.parentable_type,
        });

        // Set parent organization as a group
        if (userData.parentable_type && userData.parentable_id) {
          posthog?.group(
            userData.parentable_type.toLowerCase(),
            userData.parentable_id,
          );
        }

        posthog?.capture("login_succeeded", {
          email: userData.email,
          role: userData.role,
          parentable_type: userData.parentable_type,
        });

        dispatch({ type: "SET_USER", payload: { user: userData, token } });

        // Get the intended path from location state or default to "/"
        const from =
          (location.state as { from?: { pathname?: string } } | null)?.from
            ?.pathname || "/";
        void navigate(isApOnly(userData.role) ? "/" : from, { replace: true });
      } catch (error) {
        posthog?.capture("login_failed", { email: credentials.email });
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        dispatch({ type: "LOGOUT" });
        throw error;
      }
    },
    [
      publicApi,
      navigate,
      location,
      posthog,
      openResetCodeEntry,
      sendPasswordResetCode,
    ],
  );

  const logout = useCallback(async () => {
    try {
      // End impersonation session if currently impersonating
      if (state.isImpersonating && state.token) {
        try {
          await publicApi.delete("/admin_impersonation", {
            headers: {
              Authorization: `Bearer ${state.token}`,
            },
          });
        } catch (error) {
          // Don't throw - we still want to log out even if this fails
          console.error("Failed to end impersonation during logout:", error);
        }
      }

      // Regular logout
      if (state.token) {
        await publicApi.delete("/logout", {
          headers: {
            Authorization: `Bearer ${state.token}`,
          },
        });
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      posthog?.capture("logout");
      queryClient.clear();
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      dispatch({ type: "LOGOUT" });
      void navigate("/login");
    }
  }, [
    state.isImpersonating,
    state.token,
    publicApi,
    posthog,
    queryClient,
    navigate,
  ]);

  const refreshToken = useCallback(async (): Promise<string | null> => {
    try {
      if (refreshPromise.current) {
        return refreshPromise.current;
      }

      refreshPromise.current = (async () => {
        const newToken = await refreshAcrossTabs(
          localStorage.getItem("token"),
          async () => {
            const { headers } = await publicApi.post("/token_refresh", {});
            const token = headers.authorization?.replace("Bearer ", "");
            if (!token) throw new Error("No token received from refresh");
            return token;
          },
        );

        if (!state.user) throw new Error("No user available for refresh");
        const user = { ...state.user, role: getTokenRole(newToken) };

        localStorage.setItem("user", JSON.stringify(user));
        queryClient.setQueryData<DetailedUser>(userKey(user.id), (current) =>
          current ? getEnrichedUser({ ...current, role: user.role }) : current,
        );
        dispatch({ type: "SET_USER", payload: { user, token: newToken } });
        return newToken;
      })();

      const result = await refreshPromise.current;
      refreshPromise.current = null;
      return result;
    } catch (error) {
      console.error("Token refresh failed:", error);
      refreshPromise.current = null;
      await logout();
      throw error;
    }
  }, [publicApi, logout, state.user, queryClient]);

  const resetPassword = useCallback(
    async (credentials: ResetPasswordCredentials) => {
      try {
        await publicApi.post("/password_reset/submit", credentials);
      } catch (error) {
        console.error("Password reset submission failed:", error);
        throw getPasswordResetSubmissionError(error);
      }

      dispatch({ type: "CLEAR_PASSWORD_RESET" });

      try {
        await login({
          email: credentials.email,
          password: credentials.password,
        });
      } catch (error) {
        console.error("Automatic login after password reset failed:", error);
        throw new PasswordResetError(
          PASSWORD_RESET_ERROR_CODE.loginFailed,
          "Your password was changed, but we couldn't sign you in automatically. Please log in with your new password.",
        );
      }
    },
    [publicApi, login],
  );

  const handleImpersonationChange = useCallback(
    (impState: {
      isImpersonating: boolean;
      impersonatedUser: IUser | null;
    }) => {
      if (impState.isImpersonating) {
        dispatch({
          type: "SET_IMPERSONATION",
          payload: { user: impState.impersonatedUser! },
        });
      } else {
        dispatch({ type: "CLEAR_IMPERSONATION" });
      }
    },
    [],
  );

  const contextValue = {
    ...state,
    user: state.isImpersonating ? state.impersonatedUser : state.user,
    signedInUser: state.user,
    login,
    logout,
    refreshToken,
    forgotPassword,
    openResetCodeEntry,
    getResetCodeResendAvailableAt,
    resetPasswordCode,
    verifyResetPasswordCode,
    resetPassword,
    handleImpersonationChange,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default AuthProvider;
